import { Box, Flex, Text } from '@mantine/core'
import { useEffect } from 'react'

import { useTranslation } from '@flecto/translations'
import { NumberInputWithTooltip } from '@flecto/ui'

import '../SelectPricingTiers/styles.module.css'

import useMediaQuery from '@flecto/ui/lib/utils/useMediaQuery'

import { InputTaxesSelector } from '../../../taxes'
import { useBookingItemAvailabilityCalculator } from '../../hooks/useBookingItemAvailabilityCalculator'
import { TooltipBookingProductOverbooking } from '../TooltipBookingProductOverbooking/TooltipBookingProductOverbooking'
import { TooltipBookingProductUnavailable } from '../TooltipBookingProductUnavailable/TooltipBookingProductUnavailable'
import { useFormContext } from './FormBookingProduct'

type FormSectionQuantityProps = {
  resetCustomValue: boolean
}
export function FormSectionQuantity({
  resetCustomValue,
}: FormSectionQuantityProps) {
  const { t } = useTranslation('saas', { keyPrefix: 'booking_items' })
  const { isMobile } = useMediaQuery()
  const form = useFormContext()

  const {
    quantity,
    isUnavailable,
    isOverbooked,
    isInfinite,
    infiniteSymbol,
    remainingQuantity,
    inputMaxQuantity,
    updateQuantity,
    colorAvailability,
  } = useBookingItemAvailabilityCalculator({
    bookingId: form.values.booking?.internal_id,
    itemId: form.values.product?.id,
    quantityStart: form.values.quantity ?? 1,
  })

  const hasPickedUpQuantity =
    form?.values?.picked_up_quantity !== undefined &&
    form.values.picked_up_quantity === form.values.quantity

  useEffect(() => {
    form.setFieldValue('quantity', quantity)
  }, [quantity])

  useEffect(() => {
    if (resetCustomValue) {
      updateQuantity(form.values.quantity ?? 0)
    }
  }, [resetCustomValue])

  return (
    <Box>
      <Flex
        align='start'
        justify='space-between'
        gap='md'
        wrap={isMobile ? 'wrap' : 'nowrap'}
      >
        <Flex direction='column' w='100%'>
          <Flex gap='xs'>
            <Text fw={500}>{t('label_quantity')}</Text>
            <Text fw={500} c='red'>
              *
            </Text>
          </Flex>
          <Text c='dimmed' size='sm'>
            {t('label_quantity_placeholder')}
          </Text>
          <NumberInputWithTooltip
            value={quantity}
            updateValue={updateQuantity}
            maxValue={inputMaxQuantity}
            isInfinite={isInfinite}
            hasPickedUpQuantity={hasPickedUpQuantity}
            remainingValue={remainingQuantity}
            tooltipKey='tooltip_picked_up_quantity'
            reservedQuantity={form?.values?.picked_up_quantity as number}
          />

          <Flex align='center'>
            {isUnavailable && (
              <TooltipBookingProductUnavailable withIcon={false} />
            )}
            {isOverbooked && (
              <TooltipBookingProductOverbooking withIcon={false} />
            )}
            <Text c={colorAvailability}>
              {isOverbooked || isUnavailable ? ': ' : ''}
              {remainingQuantity <= 1
                ? t('label_item_quantity_available', {
                    replace: {
                      count: isInfinite ? infiniteSymbol : remainingQuantity,
                    },
                  })
                : t('label_item_quantity_available_plural', {
                    replace: {
                      count: isInfinite ? infiniteSymbol : remainingQuantity,
                    },
                  })}
            </Text>
          </Flex>
        </Flex>
        <Flex direction='column' w='100%'>
          <Text fw={500}>{t('label_tax')}</Text>
          <Text c='dimmed' size='sm'>
            {t('label_tax_placeholder')}
          </Text>
          <InputTaxesSelector
            data-testid='input-taxes'
            disabled={!form.values.taxable}
            w={250}
            {...form.getInputProps('tax_rate')}
          />
        </Flex>
      </Flex>
    </Box>
  )
}
